import type { LocalMaterialRecord, VaultWriteResult } from '../types'

type VaultPermissionState = 'granted' | 'denied' | 'prompt'

type VaultPermissionDescriptor = {
  mode: 'read' | 'readwrite'
}

type LocalWritableFileStream = {
  write: (data: Blob | string) => Promise<void>
  close: () => Promise<void>
}

type LocalFileHandle = {
  createWritable: () => Promise<LocalWritableFileStream>
}

export type LocalDirectoryHandle = {
  kind: 'directory'
  name: string
  getDirectoryHandle: (
    name: string,
    options?: { create?: boolean },
  ) => Promise<LocalDirectoryHandle>
  getFileHandle: (
    name: string,
    options?: { create?: boolean },
  ) => Promise<LocalFileHandle>
  queryPermission?: (
    descriptor: VaultPermissionDescriptor,
  ) => Promise<VaultPermissionState>
  requestPermission?: (
    descriptor: VaultPermissionDescriptor,
  ) => Promise<VaultPermissionState>
}

type WindowWithDirectoryPicker = Window & {
  showDirectoryPicker?: (options: {
    id?: string
    mode?: 'read' | 'readwrite'
    startIn?: 'documents' | 'desktop'
  }) => Promise<LocalDirectoryHandle>
}

export const OBSIDIAN_HOME_URL =
  'obsidian://open?vault=%E7%9F%A5%E8%AF%86%E5%BA%93&file=00_%E5%AF%BC%E8%88%AA%2F%E7%9F%A5%E8%AF%86%E5%BA%93%E9%A6%96%E9%A1%B5'

export function supportsObsidianFolderAccess() {
  return typeof (window as WindowWithDirectoryPicker).showDirectoryPicker === 'function'
}

export async function getVaultPermission(
  handle: LocalDirectoryHandle,
  request: boolean,
) {
  const descriptor: VaultPermissionDescriptor = { mode: 'readwrite' }
  const current = handle.queryPermission
    ? await handle.queryPermission(descriptor)
    : 'prompt'

  if (current === 'granted' || !request || !handle.requestPermission) {
    return current
  }

  return handle.requestPermission(descriptor)
}

export async function pickObsidianVault() {
  const picker = (window as WindowWithDirectoryPicker).showDirectoryPicker

  if (!picker) {
    throw new Error('当前浏览器不支持本地文件夹授权。')
  }

  const handle = await picker({
    id: 'studna-obsidian-vault',
    mode: 'readwrite',
    startIn: 'documents',
  })

  try {
    await handle.getDirectoryHandle('.obsidian')
  } catch {
    throw new Error('请选择“知识库”根目录，目录内应包含 .obsidian 文件夹。')
  }

  return handle
}

function safeFileName(value: string) {
  const cleaned = value
    .normalize('NFKC')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_\.]+|[_\.]+$/g, '')

  return cleaned || '图片资料'
}

function yamlString(value: string) {
  return JSON.stringify(value)
}

function buildVaultNote(
  record: LocalMaterialRecord,
  assetPath: string,
) {
  const relatedIds = `[${record.related_knowledge_ids.join(', ')}]`

  return `---
id: ${record.id}
entity_type: material
title: ${yamlString(record.title)}
student_id: ${record.student_id}
subject: ${record.subject}
grade: ${record.grade}
material_type: ${record.material_type}
source_scene: 驾驶舱图片录入
material_date: ${record.material_date}
original_file_name: ${yamlString(record.original_file_name)}
mime_type: ${record.mime_type}
file_size: ${record.file_size}
related_knowledge_ids: ${relatedIds}
processing_status: 待处理
content_status: raw
review_level: L0
audit_status: 待审核
copyright_status: ${yamlString(record.copyright_status)}
storage_scope: local_vault
storage_key: ${yamlString(assetPath)}
created_at: ${record.created_at}
updated_at: ${record.updated_at}
tags: [图片资料, 驾驶舱录入, 待处理]
---

# ${record.title}

![[${assetPath}]]

## 资料信息

- 类型：${record.material_type}
- 关联知识：${record.related_knowledge_ids.length > 0 ? record.related_knowledge_ids.join('、') : '暂未关联'}
- 备注：${record.notes || '无'}

## 待处理动作

- [ ] 检查图片清晰度和方向
- [ ] 识别题目或知识内容
- [ ] 关联知识点与错因
- [ ] 决定是否进入正式教学库或题库
`
}

export async function writeMaterialToObsidian(
  handle: LocalDirectoryHandle,
  record: LocalMaterialRecord,
): Promise<VaultWriteResult> {
  const permission = await getVaultPermission(handle, false)

  if (permission !== 'granted') {
    throw new Error('Obsidian 文件夹需要重新授权。')
  }

  const assetsDirectory = await handle.getDirectoryHandle('assets', {
    create: true,
  })
  const inboxDirectory = await handle.getDirectoryHandle('90_待整理', {
    create: true,
  })
  const timestamp = record.created_at
    .replace(/[-:]/g, '')
    .replace('T', '-')
    .slice(0, 15)
  const originalName = safeFileName(record.original_file_name)
  const assetName = `${timestamp}_${originalName}`
  const assetPath = `assets/${assetName}`
  const noteBaseName = safeFileName(
    originalName.replace(/\.[^.]+$/, ''),
  )
  const noteName = `图片资料_${timestamp}_${noteBaseName}.md`
  const notePath = `90_待整理/${noteName}`

  const assetHandle = await assetsDirectory.getFileHandle(assetName, {
    create: true,
  })
  const assetWritable = await assetHandle.createWritable()
  await assetWritable.write(record.file_blob)
  await assetWritable.close()

  const noteHandle = await inboxDirectory.getFileHandle(noteName, {
    create: true,
  })
  const noteWritable = await noteHandle.createWritable()
  await noteWritable.write(buildVaultNote(record, assetPath))
  await noteWritable.close()

  return { assetPath, notePath }
}
